import { useState } from "react";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Users,
  Plus,
  Pencil,
  Trash2,
  Search,
  Target,
  MessageCircle,
  AlertTriangle,
  Heart,
  X,
  Check,
  ChevronDown,
  Briefcase,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { toUserFriendlyError } from "@/lib/error-messages";

const TONE_OPTIONS = [
  { value: "formal", label: "Formal" },
  { value: "informal", label: "Informal" },
  { value: "tecnico", label: "Técnico" },
  { value: "amigavel", label: "Amigável" },
  { value: "descontraido", label: "Descontraído" },
] as const;

type ToneValue = typeof TONE_OPTIONS[number]["value"];

const emptyForm = {
  name: "",
  description: "",
  painPoints: "",
  desires: "",
  commonObjections: "",
  preferredTone: "formal" as ToneValue,
  ageRange: "",
  role: "",
  segmentId: undefined as number | undefined,
};

type FormState = typeof emptyForm;

export function AudiencesPanel() {
  const [search, setSearch] = useState("");
  const [editingId, setEditingId] = useState<number | null>(null);
  const [creating, setCreating] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);

  const utils = trpc.useUtils();
  const { data: audiences = [], isLoading } = trpc.aiEmail.listAudiences.useQuery();
  const { data: segments = [] } = trpc.segments.list.useQuery();

  const createMutation = trpc.aiEmail.createAudience.useMutation({
    onSuccess: () => {
      utils.aiEmail.listAudiences.invalidate();
      closeForm();
      toast.success("Público-alvo criado!");
    },
    onError: (e) => toast.error(toUserFriendlyError(e)),
  });

  const updateMutation = trpc.aiEmail.updateAudience.useMutation({
    onSuccess: () => {
      utils.aiEmail.listAudiences.invalidate();
      closeForm();
      toast.success("Público-alvo atualizado!");
    },
    onError: (e) => toast.error(toUserFriendlyError(e)),
  });

  const deleteMutation = trpc.aiEmail.deleteAudience.useMutation({
    onSuccess: () => {
      utils.aiEmail.listAudiences.invalidate();
      toast.success("Público-alvo removido!");
      setConfirmDeleteId(null);
    },
    onError: (e) => toast.error(toUserFriendlyError(e)),
  });

  function closeForm() {
    setEditingId(null);
    setCreating(false);
    setForm(emptyForm);
  }

  function startCreate() {
    setForm(emptyForm);
    setEditingId(null);
    setCreating(true);
  }

  function startEdit(audience: typeof audiences[number]) {
    setForm({
      name: audience.name,
      description: audience.description ?? "",
      painPoints: (audience.painPoints as string[] | null)?.join("\n") ?? "",
      desires: (audience.desires as string[] | null)?.join("\n") ?? "",
      commonObjections: (audience.commonObjections as string[] | null)?.join("\n") ?? "",
      preferredTone: audience.preferredTone as ToneValue,
      ageRange: audience.ageRange ?? "",
      role: audience.role ?? "",
      segmentId: audience.segmentId ?? undefined,
    });
    setEditingId(audience.id);
    setCreating(false);
  }

  function handleSubmit() {
    const toArray = (s: string) => s.split("\n").map((l) => l.trim()).filter(Boolean);
    const data = {
      name: form.name,
      description: form.description || undefined,
      painPoints: toArray(form.painPoints).length ? toArray(form.painPoints) : undefined,
      desires: toArray(form.desires).length ? toArray(form.desires) : undefined,
      commonObjections: toArray(form.commonObjections).length ? toArray(form.commonObjections) : undefined,
      preferredTone: form.preferredTone,
      ageRange: form.ageRange || undefined,
      role: form.role || undefined,
      segmentId: form.segmentId || undefined,
    };

    if (editingId) {
      updateMutation.mutate({ id: editingId, ...data });
    } else {
      createMutation.mutate(data);
    }
  }

  const filtered = audiences.filter((a) => a.name.toLowerCase().includes(search.toLowerCase()));
  const formOpen = creating || editingId !== null;
  const formTitle = editingId ? "Editar Público-Alvo" : "Nova Persona";

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-lg font-semibold">Públicos-Alvo</h2>
          <p className="text-sm text-muted-foreground">
            Defina personas para a IA adaptar tom, linguagem e argumentos por canal.
          </p>
        </div>
        {!formOpen && (
          <Button onClick={startCreate}>
            <Plus className="w-4 h-4 mr-2" /> Nova Persona
          </Button>
        )}
      </div>

      {formOpen && (
        <Card className="p-5 border-primary/40 bg-primary/[0.02]">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-sm">{formTitle}</h3>
            <Button variant="ghost" size="icon" onClick={closeForm} className="h-7 w-7" aria-label="Fechar">
              <X className="w-4 h-4" />
            </Button>
          </div>
          <div className="space-y-4">
            <div>
              <Label>Nome *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ex: Gestor de Marketing PME" />
            </div>
            <div>
              <Label>Descrição</Label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Quem é essa persona? Contexto geral..." rows={2} />
            </div>
            <div>
              <Label>Dores / Problemas (um por linha)</Label>
              <Textarea value={form.painPoints} onChange={(e) => setForm({ ...form, painPoints: e.target.value })} placeholder="Não consegue gerar leads qualificados&#10;Falta de tempo para criar conteúdo&#10;ROI baixo em campanhas" rows={3} />
            </div>
            <div>
              <Label>Desejos (um por linha)</Label>
              <Textarea value={form.desires} onChange={(e) => setForm({ ...form, desires: e.target.value })} placeholder="Automatizar processos&#10;Aumentar conversão em 30%&#10;Ter relatórios claros" rows={3} />
            </div>
            <div>
              <Label>Objeções Comuns (um por linha)</Label>
              <Textarea value={form.commonObjections} onChange={(e) => setForm({ ...form, commonObjections: e.target.value })} placeholder="É caro demais&#10;Já uso outra ferramenta&#10;Não tenho equipe técnica" rows={3} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Tom Preferido</Label>
                <Select value={form.preferredTone} onValueChange={(v) => setForm({ ...form, preferredTone: v as ToneValue })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TONE_OPTIONS.map((t) => (
                      <SelectItem key={t.value} value={t.value}>
                        {t.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Faixa Etária</Label>
                <Input value={form.ageRange} onChange={(e) => setForm({ ...form, ageRange: e.target.value })} placeholder="25-45 anos" />
              </div>
            </div>
            <div>
              <Label>Cargo / Função</Label>
              <Input value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} placeholder="Gerente de Marketing" />
            </div>
            <div>
              <Label>Segmento Vinculado</Label>
              <Select value={form.segmentId?.toString() ?? ""} onValueChange={(v) => setForm({ ...form, segmentId: v ? Number(v) : undefined })}>
                <SelectTrigger>
                  <SelectValue placeholder="Vincular a um segmento..." />
                </SelectTrigger>
                <SelectContent>
                  {segments.map((s: { id: number; name: string }) => (
                    <SelectItem key={s.id} value={s.id.toString()}>
                      {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" onClick={closeForm}>
                Cancelar
              </Button>
              <Button onClick={handleSubmit} disabled={!form.name.trim() || createMutation.isPending || updateMutation.isPending}>
                {(createMutation.isPending || updateMutation.isPending) && (
                  <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                )}
                {editingId ? "Salvar" : "Criar"}
              </Button>
            </div>
          </div>
        </Card>
      )}

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          placeholder="Buscar público-alvo..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9"
        />
      </div>

      {isLoading ? (
        <div className="text-center py-12 text-muted-foreground">Carregando...</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12">
          <Users className="w-12 h-12 mx-auto text-muted-foreground/40 mb-3" />
          <p className="text-muted-foreground">Nenhum público-alvo cadastrado</p>
          <p className="text-sm text-muted-foreground/60">Defina personas para que a IA personalize as comunicações</p>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {filtered.map((audience) => {
            const isConfirming = confirmDeleteId === audience.id;
            const isExpanded = expandedId === audience.id;
            const painPoints = (audience.painPoints as string[] | null) ?? [];
            const desires = (audience.desires as string[] | null) ?? [];
            const objections = (audience.commonObjections as string[] | null) ?? [];

            return (
              <Card
                key={audience.id}
                className={`transition-shadow hover:shadow-md ${isExpanded ? "ring-1 ring-primary/30" : ""}`}
              >
                <button
                  type="button"
                  onClick={() => setExpandedId(isExpanded ? null : audience.id)}
                  className="w-full text-left p-4 cursor-pointer"
                  aria-expanded={isExpanded}
                >
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="min-w-0 flex-1">
                      <h3 className="font-semibold text-sm leading-tight">{audience.name}</h3>
                      <div className="flex flex-wrap gap-1.5 mt-1.5">
                        <Badge variant="secondary" className="text-[10px]">
                          <MessageCircle className="w-2.5 h-2.5 mr-1" />
                          {TONE_OPTIONS.find((t) => t.value === audience.preferredTone)?.label ?? audience.preferredTone}
                        </Badge>
                        {audience.role && (
                          <Badge variant="outline" className="text-[10px]">
                            <Briefcase className="w-2.5 h-2.5 mr-1" />
                            {audience.role}
                          </Badge>
                        )}
                        {audience.ageRange && (
                          <Badge variant="outline" className="text-[10px]">{audience.ageRange}</Badge>
                        )}
                      </div>
                    </div>
                    <ChevronDown
                      className={`w-4 h-4 shrink-0 text-muted-foreground transition-transform ${isExpanded ? "rotate-180" : ""}`}
                    />
                  </div>

                  {audience.description && (
                    <p className="text-xs text-muted-foreground line-clamp-2">{audience.description}</p>
                  )}

                  <div className="grid grid-cols-3 gap-2 mt-3">
                    <Stat icon={<AlertTriangle className="w-3 h-3 text-amber-600" />} label="Dores" count={painPoints.length} />
                    <Stat icon={<Heart className="w-3 h-3 text-rose-600" />} label="Desejos" count={desires.length} />
                    <Stat icon={<Target className="w-3 h-3 text-indigo-600" />} label="Objeções" count={objections.length} />
                  </div>
                </button>

                {isExpanded && (
                  <div className="border-t px-4 py-4 space-y-4 bg-muted/10">
                    {audience.description && (
                      <div>
                        <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">Descrição</Label>
                        <p className="text-sm mt-1 leading-relaxed">{audience.description}</p>
                      </div>
                    )}

                    {painPoints.length > 0 && (
                      <DetailList
                        icon={<AlertTriangle className="w-3.5 h-3.5 text-amber-600" />}
                        label="Dores / Problemas"
                        items={painPoints}
                        bulletColor="bg-amber-500"
                      />
                    )}
                    {desires.length > 0 && (
                      <DetailList
                        icon={<Heart className="w-3.5 h-3.5 text-rose-600" />}
                        label="Desejos"
                        items={desires}
                        bulletColor="bg-rose-500"
                      />
                    )}
                    {objections.length > 0 && (
                      <DetailList
                        icon={<Target className="w-3.5 h-3.5 text-indigo-600" />}
                        label="Objeções comuns"
                        items={objections}
                        bulletColor="bg-indigo-500"
                      />
                    )}

                    <div className="flex gap-2 pt-1">
                      {!isConfirming ? (
                        <>
                          <Button size="sm" variant="outline" onClick={() => startEdit(audience)} className="h-8">
                            <Pencil className="w-3.5 h-3.5 mr-1.5" /> Editar
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-8 text-destructive hover:text-destructive"
                            onClick={() => setConfirmDeleteId(audience.id)}
                          >
                            <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Remover
                          </Button>
                        </>
                      ) : (
                        <div className="flex gap-2 items-center">
                          <span className="text-xs text-muted-foreground">Confirmar remoção?</span>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-8"
                            onClick={() => setConfirmDeleteId(null)}
                            disabled={deleteMutation.isPending}
                          >
                            <X className="w-3.5 h-3.5" />
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            className="h-8"
                            onClick={() => deleteMutation.mutate({ id: audience.id })}
                            disabled={deleteMutation.isPending}
                          >
                            {deleteMutation.isPending ? (
                              <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                            ) : (
                              <Check className="w-3.5 h-3.5 mr-1.5" />
                            )}
                            Sim, remover
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Stat({ icon, label, count }: { icon: React.ReactNode; label: string; count: number }) {
  return (
    <div className="text-[10px] flex flex-col">
      <span className="text-muted-foreground flex items-center gap-1">
        {icon} {label}
      </span>
      <span className="font-semibold text-sm">{count}</span>
    </div>
  );
}

function DetailList({
  icon,
  label,
  items,
  bulletColor,
}: {
  icon: React.ReactNode;
  label: string;
  items: string[];
  bulletColor: string;
}) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
        {icon} {label}
      </Label>
      <ul className="mt-1.5 space-y-1">
        {items.map((d, i) => (
          <li key={i} className="flex items-start gap-2 text-sm">
            <span className={`${bulletColor} w-1.5 h-1.5 rounded-full mt-2 shrink-0`} />
            <span>{d}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
